import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../features/auth/auth.service';
import { faIcons } from '../../icons/fa-icons';
 

/**
 * Where each role should land after a successful switch.
 * Adjust these paths to match your actual routes.
 */
const ROLE_HOME_ROUTES: Record<string, string> = {
  vendor:   '/vendor/dashboard',
  customer: '/customer/dashboard',
  admin:    '/admin/dashboard',
};

@Component({
    selector: 'app-role-switcher',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule],
    templateUrl: './role-switcher.component.html',
    styleUrls: ['./role-switcher.component.css']
})
export class RoleSwitcherComponent implements OnInit, OnDestroy {

    private authService = inject(AuthService);
    private router = inject(Router);

    icons = faIcons;

    currentRole: string = '';
    switchableRoles: string[] = [];
    showCanSwitch: boolean = false;

    isSwitching: boolean = false;
    switchError: string | null = null;
    dropdownOpen: boolean = false;

    private authStateSub: Subscription | null = null;

    ngOnInit(): void {
        this.refreshRoleState();

        // Rebuild whenever the session changes (login, logout, or a role
        // switch elsewhere) so this control always reflects the live session.
        this.authStateSub = this.authService.authState$.subscribe(() => {
            this.refreshRoleState();
        });
    }

    ngOnDestroy(): void {
        this.authStateSub?.unsubscribe();
    }

    private refreshRoleState(): void {
        this.currentRole    = this.authService.getUserRole();
        this.showCanSwitch  = this.authService.canSwitchRole();
        this.switchableRoles = this.authService.getSwitchableRoles();
    }

    toggleDropdown(): void {
        this.dropdownOpen = !this.dropdownOpen;
    }

    closeDropdown(): void {
        this.dropdownOpen = false;
    }

    /**
     * Calls POST /shopasst/api/auth/switch-role — no re-login/OTP needed.
     * On success, AuthService.storeSession() has already applied the new
     * token/role/pages (via switchRole()'s internal tap), so we just
     * navigate to that role's home screen.
     */
    onSwitchRole(role: string): void {
        if (this.isSwitching || role === this.currentRole) {
            this.closeDropdown();
            return;
        }

        this.isSwitching = true;
        this.switchError = null;

        this.authService.switchRole(role).subscribe({
            next: (res) => {
                this.isSwitching = false;
                this.closeDropdown();

                if (res?.success) {
                    this.refreshRoleState();
                    const homeRoute = ROLE_HOME_ROUTES[role] || '/';
                    this.router.navigateByUrl(homeRoute);
                } else {
                    this.switchError = res?.message || 'Unable to switch role. Please try again.';
                }
            },
            error: (err) => {
                this.isSwitching = false;
                this.closeDropdown();
                console.error('Role switch failed:', err);
                this.switchError = err.error?.message || 'Unable to switch role. Please try again.';
            }
        });
    }

    roleLabel(role: string): string {
        return role.charAt(0).toUpperCase() + role.slice(1);
    }
}